import cx from "classnames";
import ToolTip from "components/design/toolTip";
import { Home } from "components/icons/LeftNav/Home";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetEntityAllStateDataQuery } from "store/apis/entity";
import { getMissingRegistrations } from "util/getMissingRegistrations";
import { OverviewState } from "./brandOverview.types";
import { EMPTY_FIELD } from "./constants";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";

const ALL_SET_LABEL = "All registrations are set up";

type EntityStatesOfRegistrationProps = {
  entityId: string;
  states: OverviewState[];
};

export const EntityStatesOfRegistration = ({
  entityId,
  states,
}: EntityStatesOfRegistrationProps) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const {
    data: stateData,
    isSuccess,
    isLoading,
  } = useGetEntityAllStateDataQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  if (isLoading) {
    return <Skeleton className="t-mt-1.5 t-h-4 t-w-40 t-rounded-full" />;
  }

  if (!isSuccess) return null;

  // state name -> list of missing registrations (from data-attributes API)
  const missingByState = new Map<string, string[]>();
  (stateData ?? []).forEach((data) => {
    missingByState.set(data.state.toLowerCase(), getMissingRegistrations(data));
  });

  if (states.length === 0) {
    return <span className="t-text-body t-text-neutral-40">{EMPTY_FIELD}</span>;
  }

  return (
    <div className="t-mt-1 t-flex t-flex-wrap t-gap-2">
      {states.map((state) => {
        const missing = missingByState.get(state.name.toLowerCase()) ?? [];
        const hasMissingData = missing.length > 0;

        const tooltipContent = hasMissingData ? (
          <div>
            <p className="t-m-0 t-mb-1 t-font-medium">Missing items</p>
            <ul className="t-m-0 t-list-disc t-pl-4">
              {missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          ALL_SET_LABEL
        );

        return (
          <ToolTip
            key={state.id}
            text={tooltipContent}
            propClass="!t-text-left"
          >
            <span
              className={cx(
                "t-flex t-items-center t-gap-1 t-rounded-md t-px-1.5 t-py-0.5 t-text-body-sm",
                hasMissingData
                  ? "t-bg-red-10 t-text-red"
                  : "t-bg-dark_green-10 t-text-dark_green-70"
              )}
            >
              {state.isHome && (
                <span className="t-flex">
                  <Home size="12" />
                </span>
              )}
              {state.name}
            </span>
          </ToolTip>
        );
      })}
    </div>
  );
};
